/**
 * SOLID Review
 * S — Strong. One concern: the frontend Domain's boundary — one contracted lollipop out, and nothing in.
 * O — Strong. A new decision is a new op on the contract and a row in its context service's table; this
 *     file does not change.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. `ClientDecisions` is typed to the contract's derived interface, never to a class.
 * D — Strong. The kernel (under the uml-components carve-out), the contract, the decisions Facade and the
 *     services it is built with — no Application, Infrastructure or Framework import.
 */
import { ProvidedPort } from '@ml3d/kernel';
import { ClientDecisionsContract, type IPortClientDecisions } from '../ports/ClientDecisions.contract';
import { ClientDecisions } from '../decisions/ClientDecisions';
import { ClientServices } from '../decisions/ClientServices';

/**
 * THE FRONTEND DOMAIN AS A COMPONENT (frontend strict-ports plan, FE1, 2026-10-03). Shaped exactly like
 * the backend's `ComponentDomain`: flat port fields, no holder classes (owner's decision, 2026-10-03).
 *
 * ── REQUIRED ports — none ──────────────────────────────────────────────────────────────────────────
 *
 *   The backend's Domain has four LOCAL sockets (ids, clock, entropy, digest), because its decisions
 *   create and stamp. A client decision does neither: the server mints every id and keeps every clock
 *   that matters, and the one timestamp the client keeps (`lastGameActivityAt`) is an instruction in the
 *   changes, stamped by the store. A socket nothing reads would fail `assertAllPortsRead`.
 *
 * ── PROVIDED ports — one kernel lollipop ───────────────────────────────────────────────────────────
 *
 *   ClientDecisions : IPortClientDecisions — the Domain's whole surface, behind a runtime contract the
 *                     kernel guards: the table and an event or command in, changes and a DTO out, nothing
 *                     that is a class, a Date or a function.
 *
 * Public and readonly so `describePorts()` can see it: the walk reads own enumerable fields. The context services
 * behind the Facade are built here, once (frontend-domain-oo P5): wiring inside the box is constructor injection.
 */
export class ComponentFrontendDomain {
  readonly clientDecisionsPort = new ProvidedPort<IPortClientDecisions>('ClientDecisions', new ClientDecisions(new ClientServices()), ClientDecisionsContract);
}
